import { CSSResultGroup, html, LitElement, PropertyValues, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { CSEButton } from 'elements/button/button.js'
import { style } from './alert.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { animateTo } from 'elements/_shared/animations.js'

initializeIconsByRole({
  'cse-alert.error': { name: 'octagon-xmark', style: 'solid' },
  'cse-alert.info': { name: 'circle-info', style: 'solid' },
  'cse-alert.success': { name: 'circle-check', style: 'solid' },
  'cse-alert.warning': { name: 'triangle-exclamation', style: 'solid' },
  'cse-alert.dismiss': { name: 'xmark', style: 'regular' },
})

export const alertVariants = ['error', 'info', 'success', 'warning'] as const
export type alertVariantTypes = typeof alertVariants

@customElement('cse-alert')
export class CSEAlert extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: String })
  variant: alertVariantTypes[number] = 'info'

  @property({ type: Boolean })
  dismissable = false

  @query('slot[name="link"]')
  private linkSlot!: HTMLSlotElement

  @query('.cse-alert')
  alert!: HTMLDivElement

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)

    const assignedNodes = this.linkSlot.assignedNodes({ flatten: true })
    const assignedButtons = assignedNodes.map(this.findButton).filter(this.isButton)

    if (assignedButtons.length > 0) {
      [...assignedButtons].forEach(button => {
        button.variant = this.variant
      })
    }
  }

  private findButton(node: Node): CSEButton | null {
    const selector = 'cse-button'
    return node instanceof HTMLElement ? node.closest(selector) ?? node.querySelector(selector) : null
  }

  private isButton(button: CSEButton | null): button is CSEButton {
    return button !== null
  }

  private closeAnimation: any
  animationComplete: Promise<void> = Promise.resolve()

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties)

    this.closeAnimation = {
      keyframes: [
        { opacity: 1, scale: 1 },
        { opacity: 0, scale: 0 }
      ],
      options: { duration: 250, easing: 'ease-in-out' }
    }
  }

  override async getUpdateComplete() {
    const update = await super.getUpdateComplete()
    await this.animationComplete
    return update
  }

  private close = async () => {
    let resolve!: () => void
    this.animationComplete = new Promise((res) => { resolve = res })

    await animateTo(
      this.alert,
      this.closeAnimation.keyframes,
      this.closeAnimation.options
    ).then(() => resolve())
    this.style.display = 'none'

    this.dispatchEvent(new CSEAlertDismissedEvent(this))
  }

  override render() {
    return html`
      <div
        class="${classMap({
          'cse-alert': true,
          [`variant-${this.variant}`]: true,
        })}"
        aria-live="assertive"
      >
        <cse-icon icon-role="cse-alert.${this.variant}" size="lg"></cse-icon>
        <div class="content">
          <slot class="title font-heading-5"></slot>
          <slot name="description" class="description font-body"></slot>
          <slot name="link"></slot>
        </div>
        ${this.dismissable ? html`
        <cse-icon-button
          icon-role="cse-alert.dismiss"
          variant="primary"
          size="small"
          @click="${this.close}"
          aria-label="${t('dismiss_alert', { ns: 'cse-alert', default: 'Dismiss alert' })}"
        ></cse-icon-button>
      ` : nothing }
      </div>
    `
  }
}

export class CSEAlertDismissedEvent extends CustomEvent<{ alert: CSEAlert }> {
  static type = 'cse-alert-dismissed' as const

  constructor(alert: CSEAlert) {
    super(CSEAlertDismissedEvent.type, {
      bubbles: true,
      composed: true,
      detail: { alert },
    })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-alert': CSEAlert
  }

  interface GlobalEventHandlersEventMap {
    [CSEAlertDismissedEvent.type]: CSEAlertDismissedEvent,
  }
}
